/**
 * Sound system. Real audio (OGG files in /public/audio) played through Phaser's
 * SoundManager. Style: low-cortisol â€” see public/audio/CREDITS.md.
 *
 * Public surface (kept stable for existing callers):
 *   sound.playFire(towerKey)        // tower-specific shot sfx
 *   sound.playDeath(tier?)          // enemy death; tier = "soft"|"armor"|"boss"
 *   sound.playWave(isBoss?)         // start-of-wave horn (soft flute)
 *   sound.playUpgrade()             // tower upgrade chime
 *   sound.playUi(name)              // "click" | "buy" | "upgrade" | "waveHorn"
 *   sound.playAmbience(name, vol?)  // "fire" | "wind"; loops at low volume
 *   sound.stopAmbience(name?)       // stop one or all ambience layers
 *   sound.setMuted(b) / isMuted()
 *
 * Music director:
 *   sound.playMusic(track, opts?)   // track key from AUDIO.music; crossfades
 *   sound.stopMusic(fadeMs?)
 *   sound.setBossLayer(on)          // layer a low drone over current music
 *
 * Settings:
 *   sound.setMasterVolume(v) / setMusicVolume(v) / setSfxVolume(v)
 *   sound.setLateNight(b)           // halves effective master
 *
 * Lifecycle: BootScene calls sound.attach(this) once during create(). All
 * methods are no-ops before attach so importing the singleton at module load
 * is safe. Files that fail to load (missing on disk) are silently skipped:
 * playSfx/playMusic check `loaded` set first.
 */
import Phaser from "phaser";
import { AUDIO, AUDIO_DEFAULTS } from "../config";
import { save } from "./Save";

type TowerKey = "archer" | "cannon" | "mage" | "frost" | "lightning" | "poison" | "crossbow" | "holy" | string;
type DeathTier = "soft" | "armor" | "boss";
type MusicTrack = keyof typeof AUDIO.music;
type UiName = keyof typeof AUDIO.sfx.ui;
type AmbienceName = keyof typeof AUDIO.ambience;
type ManagedSound = Phaser.Sound.BaseSound & { volume: number };

interface MusicSlot {
  key:   string;
  track: MusicTrack;
  inst:  ManagedSound;
  /** Active fade tweens touching this sound. Cancelled before destroy/reuse. */
  fadeTweens: Phaser.Tweens.Tween[];
  /** Browser-global timeout id for the post-fade destroy. We use setTimeout
   *  (not scene.time.delayedCall) so cleanup survives scene shutdown. */
  destroyTimeout?: ReturnType<typeof setTimeout>;
}

interface AmbienceSlot {
  inst: ManagedSound;
  fadeTweens: Phaser.Tweens.Tween[];
}

/** SFX cooldown in ms â€” same key fired faster than this is dropped. */
const SFX_DROP_MS = 30;
/** Window for shot-density tracking; >threshold shots in window triggers ducking. */
const DUCK_WINDOW_MS = 200;
const DUCK_THRESHOLD = 5;
const DUCK_AMOUNT = 0.5;
const DUCK_RECOVERY_MS = 350;

class SoundSystem {
  private scene: Phaser.Scene | null = null;
  private mgr: Phaser.Sound.BaseSoundManager | null = null;
  private sfxLastPlayed = new Map<string, number>();
  private recentShots: number[] = [];
  private duckUntil = 0;
  private music: MusicSlot | null = null;
  private bossLayer: MusicSlot | null = null;
  private bossLayerOn = false;
  /** Active ambience loops keyed by ambience name (e.g. "wind", "fire"). */
  private ambience = new Map<string, AmbienceSlot>();
  /** Music instances that are mid-fade-out. Tracked so we can hard-stop them
   *  if a new playMusic arrives before the fade finishes (the cause of
   *  stacked layers / "music lag" after several scene restarts). */
  private fadingOut: MusicSlot[] = [];
  /** Hard cap on simultaneously decoding music instances. Anything beyond
   *  this is stopped immediately rather than allowed to continue fading. */
  private static readonly MAX_FADING_OUT = 1;
  /** Set once per AudioContext: prevents attach() from re-installing the
   *  resume listeners across every scene transition. */
  private resumeInstalled = false;
  /** If the audio context is locked, the most recent playMusic request is
   *  parked here and re-issued on first user gesture. */
  private pendingMusic: { track: MusicTrack; opts: { fadeMs?: number; loop?: boolean } } | null = null;
  /** Music is no longer preloaded at boot (portal initial-download budget).
   *  Tracks mid-download live here so we don't queue the same files twice. */
  private loadingTracks = new Set<MusicTrack>();
  /** Most recent playMusic target — a lazy load that finishes late must not
   *  override a track that was requested after it. */
  private lastRequestedTrack: MusicTrack | null = null;
  /** Desired boss-layer state, tracked separately from bossLayerOn so a lazy
   *  bossDrone download only starts the layer if it is still wanted. */
  private bossLayerWanted = false;
  /** Listeners notified when mute state or volumes change. UI uses this. */
  private changeListeners: Array<() => void> = [];

  onChange(fn: () => void): () => void {
    this.changeListeners.push(fn);
    return () => { this.changeListeners = this.changeListeners.filter(f => f !== fn); };
  }
  private notifyChange(): void { for (const f of this.changeListeners) f(); }

  attach(scene: Phaser.Scene): void {
    // Re-bind tween source to the current live scene every time a scene
    // mounts. The Phaser SoundManager is global (shared across scenes), but
    // tweens are per-scene — fades created by a since-stopped scene never
    // run. Calling attach() in every scene.create() keeps fades working.
    this.scene = scene;
    this.mgr = scene.sound;

    // Resume audio context on first user gesture (browser autoplay policy).
    // Also re-issue any music request that was parked because the context
    // was locked when playMusic() ran (which happens before the user clicks).
    // Install ONCE per game session: re-installing on every scene change
    // accumulated identical pointerdown listeners on every new scene's input
    // emitter (they died with their scene, but the pattern was wasteful and
    // hid bugs). Re-issue any pending music if a scene mounted while the
    // context was still locked.
    const replayPending = () => {
      if (!this.pendingMusic) return;
      const p = this.pendingMusic;
      this.pendingMusic = null;
      // Drop any silent placeholder so playMusic starts a fresh instance now
      // that the context is running.
      this.hardStopMusic();
      this.playMusic(p.track, p.opts);
    };

    if (!this.resumeInstalled) {
      this.resumeInstalled = true;
      const resume = () => {
        const ctx = (this.mgr as unknown as { context?: AudioContext }).context;
        if (ctx && ctx.state === "suspended") {
          ctx.resume().then(replayPending).catch(() => {});
        } else {
          replayPending();
        }
      };
      // Bind to the underlying DOM so the listeners survive scene transitions.
      // Phaser routes pointer events through the canvas, so a single window
      // listener catches the same first-gesture without needing per-scene wiring.
      const opts: AddEventListenerOptions = { once: true, capture: true };
      window.addEventListener("pointerdown", resume, opts);
      window.addEventListener("keydown",     resume, opts);
      window.addEventListener("touchstart",  resume, opts);
    } else {
      // A new scene mounted; if a request is pending and the context is now
      // running (because the user has clicked at some earlier point), kick it.
      replayPending();
    }
  }

  // ---------------- Settings ----------------

  /**
   * Defensive accessor. A stale localStorage save from a pre-audio build
   * could have `data.audio === undefined`; falling back to AUDIO_DEFAULTS
   * here keeps the menu from crashing in that case.
   */
  private audio() {
    const a = save.get().audio;
    if (a) return a;
    save.get().audio = { ...AUDIO_DEFAULTS };
    return save.get().audio;
  }

  setMuted(m: boolean): void {
    this.audio().muted = m;
    save.markDirty();
    this.applyMusicVolume();
    this.notifyChange();
  }
  isMuted(): boolean { return this.audio().muted; }
  toggleMuted(): boolean { this.setMuted(!this.isMuted()); return this.isMuted(); }

  setMasterVolume(v: number): void { this.audio().master = clamp01(v); save.markDirty(); this.applyMusicVolume(); this.notifyChange(); }
  setMusicVolume(v: number):  void { this.audio().music  = clamp01(v); save.markDirty(); this.applyMusicVolume(); this.notifyChange(); }
  setSfxVolume(v: number):    void { this.audio().sfx    = clamp01(v); save.markDirty(); this.notifyChange(); }
  setLateNight(b: boolean):   void { this.audio().lateNight = b;       save.markDirty(); this.applyMusicVolume(); this.notifyChange(); }

  private effectiveMaster(): number {
    const a = this.audio();
    if (a.muted) return 0;
    return a.master * (a.lateNight ? 0.5 : 1);
  }
  private musicVol(): number { return this.effectiveMaster() * this.audio().music; }
  private sfxVol():   number { return this.effectiveMaster() * this.audio().sfx; }

  // ---------------- SFX ----------------

  playFire(towerKey: TowerKey): void {
    const map = AUDIO.sfx.tower as Record<string, string>;
    const path = map[towerKey];
    if (!path) return;
    this.recentShots.push(now());
    this.trimRecentShots();
    this.playSfx(path, { rate: rand(0.97, 1.03) });
  }

  playDeath(tier: DeathTier = "soft"): void {
    const path =
      tier === "boss"  ? AUDIO.sfx.enemy.deathBoss  :
      tier === "armor" ? AUDIO.sfx.enemy.deathArmor :
                         AUDIO.sfx.enemy.deathSoft;
    this.playSfx(path, { rate: rand(0.96, 1.04), volumeMult: 0.9 });
  }

  playWave(isBoss = false): void {
    // Boss waves get the heavier war horn; fall back to the flute if the horn
    // file is missing so a boss wave is never silent.
    const horn = isBoss && this.isLoaded(AUDIO.sfx.ui.bossHorn)
      ? AUDIO.sfx.ui.bossHorn
      : AUDIO.sfx.ui.waveHorn;
    this.playSfx(horn, { volumeMult: isBoss ? 1.0 : 0.85 });
  }

  playAbility(name: keyof typeof AUDIO.sfx.ability): void {
    this.playSfx(AUDIO.sfx.ability[name]);
  }

  playUpgrade(): void { this.playSfx(AUDIO.sfx.ui.upgrade); }

  playUi(name: UiName): void {
    const path = AUDIO.sfx.ui[name];
    if (path) this.playSfx(path);
  }

  /**
   * Loop an ambience track at a low default volume. Idempotent per name —
   * calling twice with the same name does nothing if already playing. Variants
   * are picked at random (e.g. wind has alt loops).
   */
  playAmbience(name: AmbienceName, volumeMult = 0.35): void {
    if (!this.scene || !this.mgr) return;
    if (this.ambience.has(name)) return;
    const variants = AUDIO.ambience[name] as readonly string[];
    const ready = variants.filter(k => this.isLoaded(k));
    if (!ready.length) return;
    const key = ready[Math.floor(Math.random() * ready.length)];
    const target = this.sfxVol() * volumeMult;
    const inst = this.mgr.add(key, { loop: true, volume: 0 }) as ManagedSound;
    const slot: AmbienceSlot = { inst, fadeTweens: [] };
    inst.play();
    this.ambience.set(name, slot);
    this.rememberFade(slot, this.fadeIn(inst, target, 1200));
  }

  stopAmbience(name?: AmbienceName): void {
    if (!name) {
      for (const [k, slot] of this.ambience) { this.killAmbienceSlot(slot); void k; }
      this.ambience.clear();
      return;
    }
    const slot = this.ambience.get(name);
    if (!slot) return;
    this.ambience.delete(name);
    this.killAmbienceSlot(slot);
  }

  private playSfx(key: string, opts: { rate?: number; volumeMult?: number } = {}): void {
    if (!this.mgr || !this.isLoaded(key)) return;
    const t = now();
    const last = this.sfxLastPlayed.get(key) ?? -Infinity;
    if (t - last < SFX_DROP_MS) return;
    this.sfxLastPlayed.set(key, t);

    const ducked = t < this.duckUntil ? DUCK_AMOUNT : 1;
    const vol = this.sfxVol() * (opts.volumeMult ?? 1) * ducked;
    if (vol <= 0) return;
    this.mgr.play(key, { volume: vol, rate: opts.rate ?? 1 });
  }

  private trimRecentShots(): void {
    const t = now();
    const cutoff = t - DUCK_WINDOW_MS;
    while (this.recentShots.length && this.recentShots[0] < cutoff) this.recentShots.shift();
    if (this.recentShots.length >= DUCK_THRESHOLD) {
      this.duckUntil = t + DUCK_RECOVERY_MS;
    }
  }

  // ---------------- Music ----------------

  /** True if Phaser's audio cache holds a decoded entry for this key. */
  private isLoaded(key: string): boolean {
    const cache = this.scene?.cache?.audio;
    return !!cache && cache.has(key);
  }

  /** Queue a track's variant files on the attached scene's loader and run
   *  onReady when the loader drains. Failed downloads resolve too — the
   *  retry then finds no loaded variant and stays silent (same tolerance as
   *  the old boot-time preload). */
  private lazyLoadTrack(track: MusicTrack, onReady: () => void): void {
    if (!this.scene || this.loadingTracks.has(track)) return;
    const variants = AUDIO.music[track] as readonly string[];
    const missing = variants.filter(k => !this.isLoaded(k));
    if (!missing.length) return;
    this.loadingTracks.add(track);
    const loader = this.scene.load;
    for (const path of missing) loader.audio(path, path);
    loader.once(Phaser.Loader.Events.COMPLETE, () => {
      this.loadingTracks.delete(track);
      onReady();
    });
    loader.start();
  }

  /** Pick a random variant key for a music slot that actually finished loading. */
  private pickVariant(track: MusicTrack): string | null {
    const variants = AUDIO.music[track] as readonly string[];
    if (!variants?.length) return null;
    const ready = variants.filter(k => this.isLoaded(k));
    if (!ready.length) return null;
    return ready[Math.floor(Math.random() * ready.length)];
  }

  playMusic(track: MusicTrack, opts: { fadeMs?: number; loop?: boolean } = {}): void {
    if (!this.scene || !this.mgr) return;
    this.lastRequestedTrack = track;
    // Dedupe by TRACK identity, not by the random variant key. pickVariant()
    // rolls a new variant on every call, so comparing variant keys would
    // (almost) never match — that was the source of the music-stacking bug:
    // every wave-start / scene-restart spawned a fresh instance on top of
    // the running one.
    if (this.music?.track === track && !this.pendingMusic) return;

    const key = this.pickVariant(track);
    if (!key) {
      // Variants not downloaded yet — fetch them and re-issue, unless a
      // different track was requested while the download was in flight.
      this.lazyLoadTrack(track, () => {
        if (this.lastRequestedTrack === track) this.playMusic(track, opts);
      });
      return;
    }

    // Browser autoplay: if the audio context is still locked we cannot
    // actually start playback yet. Park the request; the input listener
    // installed in attach() will replay it on first user gesture.
    const ctx = (this.mgr as unknown as { context?: AudioContext }).context;
    if (ctx && ctx.state === "suspended") {
      this.pendingMusic = { track, opts };
      return;
    }

    const fade = opts.fadeMs ?? 1800;
    const loop = opts.loop ?? true;
    const targetVol = this.musicVol();

    const inst = this.mgr.add(key, { loop, volume: 0 }) as ManagedSound;
    inst.play();

    const old = this.music;
    const next: MusicSlot = { key, track, inst, fadeTweens: [] };
    this.music = next;

    if (old) {
      this.retireSlot(old, fade);
      const tween = this.crossfade(old.inst, this.readVolume(old.inst), inst, targetVol, fade);
      this.rememberFade(old, tween);
      this.rememberFade(next, tween);
    } else {
      this.rememberFade(next, this.fadeIn(inst, targetVol, fade));
    }
  }

  /**
   * Park a slot for fade-out cleanup. Kills any in-flight fade on that slot
   * (so we don't pile up tweens writing to the same volume), enforces a hard
   * cap on simultaneously fading-out instances (anything beyond the cap is
   * stopped immediately), and schedules destroy via setTimeout so cleanup
   * survives scene teardown (the bug where R-restart leaked the previous
   * music instance).
   */
  private retireSlot(slot: MusicSlot, fadeMs: number): void {
    this.stopSlotTweens(slot);
    if (slot.destroyTimeout) { clearTimeout(slot.destroyTimeout); slot.destroyTimeout = undefined; }
    this.fadingOut.push(slot);
    while (this.fadingOut.length > SoundSystem.MAX_FADING_OUT) {
      const evicted = this.fadingOut.shift()!;
      this.killSlot(evicted);
    }
    slot.destroyTimeout = setTimeout(() => {
      this.fadingOut = this.fadingOut.filter(s => s !== slot);
      this.killSlot(slot);
    }, fadeMs + 50);
  }

  /** Hard-stop + destroy a music instance, swallowing any errors. */
  private killSlot(slot: MusicSlot): void {
    if (slot.destroyTimeout) { clearTimeout(slot.destroyTimeout); slot.destroyTimeout = undefined; }
    this.stopSlotTweens(slot);
    try { slot.inst.stop(); } catch { /* ignore */ }
    try { slot.inst.destroy(); } catch { /* ignore */ }
  }

  private killAmbienceSlot(slot: AmbienceSlot): void {
    this.stopSlotTweens(slot);
    try { slot.inst.stop(); } catch { /* ignore */ }
    try { slot.inst.destroy(); } catch { /* ignore */ }
  }

  /** Hard-stop the active music slot AND any fading-out slots. Used when the
   *  audio context is replayed: we want a clean slate. */
  private hardStopMusic(): void {
    if (this.music) { this.killSlot(this.music); this.music = null; }
    for (const s of this.fadingOut) this.killSlot(s);
    this.fadingOut = [];
  }

  /**
   * Equal-power crossfade. With linear ramps, two simultaneous fades create
   * a perceived ~6dB dip at the midpoint (the source of the "abrupt" feel).
   * Using cos/sin curves keeps total acoustic power constant across the
   * transition so the listener perceives a smooth handoff instead of a notch.
   * Returns the tween so callers can park it on the outgoing slot for
   * cancellation if a new playMusic arrives mid-fade.
   */
  private crossfade(
    a: ManagedSound, fromA: number,
    b: ManagedSound, toB: number,
    durationMs: number
  ): Phaser.Tweens.Tween | null {
    if (!this.scene) return null;
    return this.scene.tweens.addCounter({
      from: 0, to: 1, duration: durationMs, ease: "Linear",
      onUpdate: tween => {
        const t = tween.getValue() as number;
        // cos/sin equal-power curves; sum of squares is constant = 1.
        const fadeOut = Math.cos(t * Math.PI / 2);
        const fadeIn  = Math.sin(t * Math.PI / 2);
        const okA = this.trySetVolume(a, fromA * fadeOut);
        const okB = this.trySetVolume(b, toB * fadeIn);
        if (!okA || !okB) tween.stop();
      }
    });
  }

  /** Smooth fade-in using the sin half of the equal-power curve. */
  private fadeIn(inst: ManagedSound, target: number, durationMs: number): Phaser.Tweens.Tween | null {
    if (!this.scene) return null;
    return this.scene.tweens.addCounter({
      from: 0, to: 1, duration: durationMs, ease: "Linear",
      onUpdate: tween => {
        const t = tween.getValue() as number;
        if (!this.trySetVolume(inst, target * Math.sin(t * Math.PI / 2))) tween.stop();
      }
    });
  }

  /** Smooth fade-out using the cos half of the equal-power curve. */
  private fadeOut(inst: ManagedSound, durationMs: number, onDone: () => void): Phaser.Tweens.Tween | null {
    if (!this.scene) { onDone(); return null; }
    const fromVol = this.readVolume(inst);
    return this.scene.tweens.addCounter({
      from: 0, to: 1, duration: durationMs, ease: "Linear",
      onUpdate: tween => {
        const t = tween.getValue() as number;
        if (!this.trySetVolume(inst, fromVol * Math.cos(t * Math.PI / 2))) tween.stop();
      },
      onComplete: onDone
    });
  }

  stopMusic(fadeMs = 1200): void {
    if (!this.scene || !this.music) return;
    const old = this.music;
    this.music = null;
    this.retireSlot(old, fadeMs);
    this.rememberFade(old, this.fadeOut(old.inst, fadeMs, () => { /* destroy handled by retireSlot */ }));
    this.setBossLayer(false);
  }

  /** Layer a low drone over current music. Idempotent. */
  setBossLayer(on: boolean): void {
    if (!this.scene || !this.mgr) return;
    this.bossLayerWanted = on;
    if (on === this.bossLayerOn) return;
    this.bossLayerOn = on;

    if (on) {
      const key = this.pickVariant("bossDrone");
      if (!key) {
        // Drone files not downloaded yet — roll back and retry once they
        // land, but only if the layer is still wanted by then.
        this.bossLayerOn = false;
        this.lazyLoadTrack("bossDrone", () => {
          if (this.bossLayerWanted) this.setBossLayer(true);
        });
        return;
      }
      const target = this.musicVol() * 0.6;
      const inst = this.mgr.add(key, { loop: true, volume: 0 }) as ManagedSound;
      const slot: MusicSlot = { key, track: "bossDrone", inst, fadeTweens: [] };
      inst.play();
      this.bossLayer = slot;
      this.rememberFade(slot, this.fadeIn(inst, target, 1600));
    } else if (this.bossLayer) {
      const old = this.bossLayer;
      this.bossLayer = null;
      this.retireSlot(old, 1200);
      this.rememberFade(old, this.fadeOut(old.inst, 1200, () => { /* destroy handled by retireSlot */ }));
    }
  }

  /** Re-apply current volume settings to live music + drone. Cheap. */
  private applyMusicVolume(): void {
    const v = this.musicVol();
    if (this.music && !this.trySetVolume(this.music.inst, v)) this.music = null;
    if (this.bossLayer && !this.trySetVolume(this.bossLayer.inst, v * 0.6)) this.bossLayer = null;
  }

  private rememberFade(slot: { fadeTweens: Phaser.Tweens.Tween[] }, tween: Phaser.Tweens.Tween | null): void {
    if (tween) slot.fadeTweens.push(tween);
  }

  private stopSlotTweens(slot: { fadeTweens: Phaser.Tweens.Tween[] }): void {
    const tweens = slot.fadeTweens.splice(0);
    for (const tween of tweens) {
      try { tween.stop(); } catch { /* ignore stale tween */ }
    }
  }

  private readVolume(inst: ManagedSound): number {
    try { return inst.volume; } catch { return 0; }
  }

  private trySetVolume(inst: ManagedSound, value: number): boolean {
    try {
      inst.volume = value;
      return true;
    } catch {
      return false;
    }
  }
}

function clamp01(n: number): number { return Math.max(0, Math.min(1, n)); }
function rand(a: number, b: number): number { return a + Math.random() * (b - a); }
function now(): number { return performance.now(); }

export const sound = new SoundSystem();
